import {
  Box,
  Button,
  Card,
  Checkbox,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useMemo } from "react";
import { t } from "i18next";
import { CraWaySnapshot } from "../../../../../coding/models/CraWaySnapshot";
import { CraWayApi } from "../../../../../cra/services/CraWayApi";
import { CraWaySnapshotApi } from "../../../../../coding/services/CraWaySnapshotApi";
import { LoadingScreen } from "../../../../../shared/views/LoadingScreen/LoadingScreen";
import { useStores } from "../../../../../shared/stores/RootStore";
import { toast } from "../../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../../shared/views/CustomDialog";
import { CraWaySnapshotPreview } from "./CraWaySnapshotPreview";

interface Props {
  onClose: () => any;
}
const CodingHistoryModalBase: React.FC<Props> = ({ onClose }) => {
  const { craWayStore, codingStore } = useStores();
  const [snapshots, setSnapshots] = useState<CraWaySnapshot[]>();
  const [selectedId, setSelectedId] = useState<string>();
  const [loading, setLoading] = useState(true);

  const selectedCraWay = craWayStore.getSelected();

  useEffect(() => {
    CraWayApi.listCraWaySnapshots(selectedCraWay!.id!).then(({ data }) => {
      setSnapshots(data);
      setLoading(false);
    });
    // eslint-disable-next-line
  }, [selectedCraWay]);

  const selectedSnapshot = useMemo(
    () => snapshots?.find((snap) => snap.id === selectedId),
    [snapshots, selectedId]
  );

  const submit = async () => {
    setLoading(true);
    await CraWaySnapshotApi.apply(selectedId!)
      .then(() => {
        toast("success", t("coding:history.submitSuccess"));
        codingStore.fetchAttributesByCraWayId(selectedCraWay!.id);
        onClose();
      })
      .catch(() => {
        toast("error", t("coding:history.submitError"));
        setLoading(false);
      });
  };

  return (
    <CustomDialog
      size="xl"
      header={t("coding:history.title")}
      footer={
        <Button
          colorPalette="primary"
          disabled={!selectedId}
          loading={loading}
          onClick={submit}
        >
          Apply selected version
        </Button>
      }
      onClose={onClose}
    >
      <Box overflow="hidden" display="flex">
        <Card.Root
          variant="outline"
          display="flex"
          flexDirection="row"
          flexGrow={1}
        >
          {snapshots ? (
            <>
              <VStack
                flexGrow={1}
                flexShrink={0}
                overflow="auto"
                gap={0}
                separator={<Separator />}
                flexDirection="column-reverse"
              >
                {snapshots.map((snapshot, i) => {
                  const createdAt = new Date(snapshot.createdAt);
                  const isLast = i === snapshots.length - 1;
                  return (
                    <Box
                      key={i}
                      px={4}
                      py={2}
                      width="100%"
                      bg={
                        snapshot.id === selectedId
                          ? "primary.50"
                          : "transparent"
                      }
                    >
                      <Checkbox.Root
                        disabled={isLast}
                        name={snapshot.id}
                        checked={snapshot.id === selectedId}
                        onCheckedChange={() => {
                          setSelectedId((prev) =>
                            prev === snapshot.id ? undefined : snapshot.id
                          );
                        }}
                        colorPalette="primary"
                        width="100%"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>
                          <Box pl={2} fontSize="sm">
                            <Text>
                              {`${createdAt.toDateString()}, ${createdAt.toLocaleTimeString()}`}
                              {isLast &&
                                ` (${t("coding:history.currentVersion")})`}
                            </Text>
                          </Box>
                        </Checkbox.Label>
                      </Checkbox.Root>
                    </Box>
                  );
                })}
              </VStack>
              <Box>
                <Separator orientation="vertical" />
              </Box>
              <CraWaySnapshotPreview snapshot={selectedSnapshot} />
            </>
          ) : (
            <Box flexGrow={1} position="relative" height={32}>
              <LoadingScreen />
            </Box>
          )}
        </Card.Root>
      </Box>
    </CustomDialog>
  );
};

export const CodingHistoryModal = observer(CodingHistoryModalBase);
